import { Delete, TextToSpeech } from "assets/icons/Common";
import Hundred from "assets/Misc/MABUnits/Icon_MAB_Hundred.png";
import One from "assets/Misc/MABUnits/Icon_MAB_One.png";
import Ten from "assets/Misc/MABUnits/Icon_MAB_Ten.png";
import Thousand from "assets/Misc/MABUnits/Icon_MAB_Thousand.png";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { useTTS } from "hooks/tts";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Vector2 } from "types/Vector2";
import { WidgetFC } from "types/Widget";

import { MABColumn } from "./MABColumn";
import { MABNumberInput } from "./MABNumberInput";

type BlockPositionMap = { [key: string]: Vector2[] };

export interface MABWidgetState {
  blockPositions: BlockPositionMap;
  originalSize?: Vector2;
  target: number;
}

export const defaultBlockPositions = {
  Thousands: [],
  Hundreds: [],
  Tens: [],
  Ones: [],
};

const BlockConfig: {
  [key: string]: { imageUrl: string; blockSize: Vector2; columnRatio: number };
} = {
  Thousands: {
    imageUrl: Thousand,
    blockSize: { x: 65, y: 65 },
    columnRatio: 2,
  },
  Hundreds: { imageUrl: Hundred, blockSize: { x: 55, y: 55 }, columnRatio: 2 },
  Tens: { imageUrl: Ten, blockSize: { x: 12, y: 65 }, columnRatio: 1 },
  Ones: { imageUrl: One, blockSize: { x: 15, y: 15 }, columnRatio: 1 },
};

const tempColors = ["red", "green", "blue", "orange"];

export const getResultingNumber = (blockPositions: BlockPositionMap) => {
  return (
    1000 * blockPositions.Thousands.length +
    100 * blockPositions.Hundreds.length +
    10 * blockPositions.Tens.length +
    blockPositions.Ones.length
  );
};

const MAX_BLOCKS: any = {
  Thousands: 20,
  Hundreds: 20,
  Tens: 20,
  Ones: 20,
};

export const MAB: WidgetFC<MABWidgetState> = ({
  state: { blockPositions, originalSize, target },
  setState,
  settings,
  pixelPosition,
}) => {
  const { maxColumn, showNumbers, showResult, showTarget } = settings;
  const resultingNumber = useMemo(() => getResultingNumber(blockPositions), [blockPositions]);
  const maxIndex = Object.keys(blockPositions).indexOf(maxColumn);
  const [activeCol, setActiveCol] = useState<string | undefined>(undefined);
  const { readAloud } = useTTS();
  const [generate, setGenerate] = useState(resultingNumber);
  const [targetInvalid, setTargetInvalid] = useState(false);
  const [totalInvalid, setTotalInvalid] = useState(false);
  const maxInputNumber =
    maxColumn === "Thousands"
      ? 22220
      : Math.pow(
          10,
          Object.keys(blockPositions).length -
            Object.keys(blockPositions).indexOf(settings.maxColumn),
        ) - 1;

  useEffect(() => {
    if (
      originalSize === undefined &&
      (pixelPosition.width || 1) > 1 &&
      (pixelPosition.width || 1) > 1
    ) {
      setState({
        originalSize: { x: pixelPosition.width!, y: pixelPosition.height! },
      });
    }
  }, [pixelPosition.width, pixelPosition.height, originalSize]);

  useEffect(() => {
    if (generate !== resultingNumber) {
      const newBlockPositions = { ...blockPositions };
      let remainingValue = generate;

      Object.keys(blockPositions).forEach((key, index) => {
        const magnitude = Math.pow(10, Object.keys(blockPositions).length - index - 1);
        const maxBlocksForKey = MAX_BLOCKS[key];

        if (key === "Thousands") {
          const totalBlocks = Math.floor(remainingValue / 1000);
          const blocksInThousands = Math.min(totalBlocks, maxBlocksForKey);
          remainingValue -= blocksInThousands * 1000;

          newBlockPositions[key] = Array<Vector2>(blocksInThousands)
            .fill({ x: 0, y: 0 })
            .map((_, idx) => ({
              x:
                ((pixelPosition.width! * (BlockConfig[key].columnRatio / 4)) / scale.x) *
                (idx % 3) *
                0.2,
              y: Math.floor(idx / 3) * BlockConfig[key].blockSize.y,
            }));
        } else if (key === "Hundreds") {
          const totalBlocks = Math.floor(remainingValue / 100);
          const blocksInHundreds = Math.min(totalBlocks, maxBlocksForKey);
          remainingValue -= blocksInHundreds * 100;

          newBlockPositions[key] = Array<Vector2>(blocksInHundreds)
            .fill({ x: 0, y: 0 })
            .map((_, idx) => ({
              x:
                ((pixelPosition.width! * (BlockConfig[key].columnRatio / 4)) / scale.x) *
                (idx % 3) *
                0.2,
              y: Math.floor(idx / 3) * BlockConfig[key].blockSize.y,
            }));
        } else if (key === "Tens") {
          const totalBlocks = Math.floor(remainingValue / 10);
          const blocksInTens = Math.min(totalBlocks, maxBlocksForKey);
          remainingValue -= blocksInTens * 10;

          newBlockPositions[key] = Array<Vector2>(blocksInTens)
            .fill({ x: 0, y: 0 })
            .map((_, idx) => ({
              x:
                ((pixelPosition.width! * (BlockConfig[key].columnRatio / 4)) / scale.x) *
                (idx % 3) *
                0.2,
              y: Math.floor(idx / 3) * BlockConfig[key].blockSize.y,
            }));
        } else {
          const numBlocks = Math.floor(remainingValue / magnitude);
          remainingValue -= numBlocks * magnitude;

          newBlockPositions[key] = Array<Vector2>(numBlocks)
            .fill({ x: 0, y: 0 })
            .map((_, idx) => ({
              x:
                ((pixelPosition.width! * (BlockConfig[key].columnRatio / 4)) / scale.x) *
                (idx % 3) *
                0.2,
              y: Math.floor(idx / 3) * BlockConfig[key].blockSize.y,
            }));
        }
      });

      setBlockPositions(newBlockPositions);
    }
  }, [generate]);

  const scale: Vector2 = useMemo(() => {
    return {
      x:
        (pixelPosition.width || 1) /
        (originalSize?.x || 1) /
        (Object.keys(blockPositions).length - maxIndex),
      y: (pixelPosition.height || 1) / (originalSize?.y || 1),
    };
  }, [originalSize, pixelPosition.width, pixelPosition.height, maxIndex, blockPositions]);

  useEffect(() => {
    const newBlockPositions = { ...blockPositions };
    Object.keys(newBlockPositions).forEach((item, index) => {
      if (index < maxIndex) {
        newBlockPositions[item] = [];
      }
    });
    setBlockPositions(newBlockPositions);
  }, [maxColumn]);

  const setBlockPositions = (blockPositions: BlockPositionMap) => {
    setState({
      blockPositions,
    });
  };

  const updateBlockPositions = (key: string, positions: Vector2[]) => {
    setBlockPositions({
      ...blockPositions,
      [key]: positions,
    });
  };

  const clearAll = () => {
    setState({
      blockPositions: defaultBlockPositions,
      target: 0,
    });
  };

  const speakAloud = (number: number, isValid: boolean) => {
    if (!isValid) {
      readAloud(String(number));
    }
  };

  return (
    <OuterContainer>
      <HeaderContainer>
        {showTarget && (
          <TotalContainer>
            <p>Target:</p>
            <MABNumberInput
              type="number"
              value={target}
              setValue={(value) => setState({ target: value })}
              minValue={0}
              maxValue={maxInputNumber}
              setIsInvalid={setTargetInvalid}
            />
            <HeaderButton
              withShadow={false}
              Icon={TextToSpeech}
              onClick={() => speakAloud(target, targetInvalid)}
            />
          </TotalContainer>
        )}
        {showResult && (
          <TotalContainer>
            <p>Total:</p>
            <MABNumberInput
              type="number"
              value={resultingNumber}
              setValue={setGenerate}
              minValue={0}
              maxValue={maxInputNumber}
              setIsInvalid={setTotalInvalid}
            />
            <HeaderButton
              withShadow={false}
              Icon={TextToSpeech}
              onClick={() => speakAloud(resultingNumber, totalInvalid)}
            />
          </TotalContainer>
        )}
        <CloseButton
          disabled={Object.keys(blockPositions).every((item) => item.length === 0)}
          withShadow={false}
          Icon={Delete}
          accentColor="var(--danger)"
          onClick={clearAll}
        />
      </HeaderContainer>
      <InnerContainer key={maxIndex}>
        {Object.keys(blockPositions).map(
          (key, index) =>
            index >= maxIndex && (
              <MABColumn
                key={key}
                blockPositions={blockPositions[key]}
                setBlockPositions={updateBlockPositions.bind(null, key)}
                header={key}
                color={tempColors[index]}
                value={
                  blockPositions[key].length *
                  Math.pow(10, Object.keys(blockPositions).length - index - 1)
                }
                showNumbers={showNumbers}
                scale={scale}
                imgSrc={BlockConfig[key].imageUrl}
                size={BlockConfig[key].blockSize}
                columnRatio={BlockConfig[key].columnRatio}
                isColumnActive={key === activeCol}
                setColumnActive={setActiveCol}
                columnWidth={pixelPosition.width!}
              />
            ),
        )}
      </InnerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  padding: 3cqw;
`;

const HeaderContainer = styled(Row)`
  align-items: center;
  margin-bottom: 10px;
  height: 9cqh;
  gap: 5cqw;
  padding: 1cqh 0px;
`;

const InnerContainer = styled(Row).attrs({
  className: "no-drag",
})`
  height: 100%;
`;

const HeaderButton = styled(IconButtonInverted)`
  height: 100%;
  aspect-ratio: 1;
`;

const CloseButton = styled(IconButtonInverted)`
  height: 100%;
  aspect-ratio: 1;
  margin-left: auto;
`;

const TotalContainer = styled(Row)`
  height: 100%;
  gap: 1cqw;
  align-items: center;

  p {
    color: var(--secondary);
    font-weight: 600;
    font-size: 2.5cqw;
  }
`;
